/**
 * Bookora design tokens — the single source of truth for the whole platform.
 *
 * Everything visual in Bookora is derived from this file: the CSS custom
 * properties (`css-vars.ts`), the Tailwind preset consumed by the patient PWA
 * and the Astro site (`tailwind-preset.ts`), and the AntD v5 theme consumed by
 * the clinic and super-admin panels (`antd-theme.ts`). Nothing downstream may
 * introduce a raw colour, radius or duration — if a value is needed, it is
 * added here first.
 *
 * ## Two personalities, not one theme with a switch
 *
 * - **Light — "Editorial Coral."** A near-monochrome broadsheet: paper-white
 *   canvas, charcoal ink text, 1px ink structural borders, **2px hard offset
 *   shadows with no blur**, faintly coral-tinted lifted surfaces, and one
 *   coral accent that does all the chromatic work. Coral appears only where a
 *   section needs punctuation — never as the action itself.
 * - **Dark — "Midnight Coral."** Near-black canvas, a faint blueprint grid,
 *   surfaces lifted by a translucent wash, hairline *inset* frost edges (never
 *   solid strokes), elevation built from inset highlights plus soft outer
 *   halos, and the same coral used as atmosphere — halos and the rule under an
 *   active tab. (Not the focus ring: a coral edge on a focused field reads as
 *   an error, so the ring is achromatic in both themes.)
 *
 *   **"Frost" and "glass" here mean a PAINTED wash on an opaque ground, never
 *   a `backdrop-filter`.** SPEC §8 forbids glass-morphism by name — *"that's
 *   Karagah's language, not this one"* — and this file used to open by calling
 *   the dark theme "a frosted-glass cathedral … translucent glass surfaces",
 *   which is the same words for a different thing. That contradiction was not
 *   academic: the super-admin console read the token names as permission and
 *   shipped `backdrop-filter: blur(12px)` on its topbar, the only one in either
 *   panel. Every value in `surfaceEffects` is a colour or a gradient, every
 *   dark surface token resolves to an opaque hex (`bgElevated` `#1B1817`), and
 *   `.bk-glass` in `css-vars.ts` is a `linear-gradient` background IMAGE — so
 *   nothing here ever blurred anything. The names stay (renaming `--bk-glass*`
 *   reaches four apps); the licence to blur does not.
 *   `admin-panel/src/layout/no-glass-morphism.test.ts` asserts the property.
 *
 * One accent, two grounds: that is the whole relationship between them, and it
 * is why they share a surname. The **primary is achromatic in both** — ink on
 * paper in light, paper on ink in dark — because the coral sits at the hue a
 * destructive red lives at and a clinical tool may not have a Save button that
 * could be read as a Delete button. See the `coral` and `crimson` ramps below;
 * the argument is written out there and asserted in `a11y.test.ts`.
 *
 * The two are deliberately opposite in shape as well as colour: light rounds
 * controls at 4px and pills only tags; dark rounds buttons to a full pill and
 * cards to 16px. That inversion is a *token* (`semanticRadii`), not a decision
 * left to the apps — see the note above that export.
 *
 * **Light is the default everywhere**, including for a visitor whose OS asks
 * for dark. `css-vars.ts` emits no `prefers-color-scheme` block; dark is opt-in
 * through the toggle only.
 *
 * This module is intentionally dependency-free and side-effect-free so it can
 * be imported from a Tailwind config, a Vite app, a Next server component, an
 * Astro build step or a plain Node script without pulling anything else in.
 */
/* ------------------------------------------------------------------ */
/*  Primitive ramps                                                    */
/* ------------------------------------------------------------------ */
/**
 * Coral — the brand accent, taken straight off the logo.
 *
 * **400 is `#F97D7D` exactly: the coral petal of the mark, unmodified.** Every
 * other stop shares its OKLCH hue (21.5°) so the ramp is the logo's own colour
 * lightened and darkened, never re-hued.
 *
 * Coral is an **accent, not a fill**. `#F97D7D` measures 2.54:1 on paper, which
 * fails WCAG 2.1 §1.4.11's 3:1 for a UI object, and white on it is also 2.54:1
 * — so it can never carry a button label. Darkening it far enough to serve as a
 * primary fill (about L 67%) lands it on top of `crimson`, the destructive red,
 * at a hue distance of ~2°. In a clinic, a Save button that looks like a Delete
 * button is a safety defect, so the brand does not go there: **the primary is
 * ink and the accent is coral**, which is exactly the hierarchy the mark itself
 * draws — charcoal wordmark and dominant petal, coral as punctuation.
 *
 * Where coral does carry weight it is either as text (700 is 5.97:1 on paper
 * and 4.89:1 on the 100 wash, so it is correct inside a coral banner) or as a
 * tinted ground with ink on top (ink on 400 is 5.80:1). 500 is the one stop
 * that clears 3:1 as a graphical object and is reserved for exactly that:
 * hairlines, active rules, the focus ring in light.
 */
export const coral = {
    50: "#FFF2F2",
    100: "#FFE2E0",
    200: "#FFC8C5",
    300: "#FFA19E",
    /** The logo coral, exact. Decorative and tinted grounds — never a fill. */
    400: "#F97D7D",
    /** The one stop that clears 3:1 on paper: rules, focus ring, active edges. */
    500: "#E76064",
    600: "#D4464D",
    /** Coral as text. 5.97:1 on paper, 4.89:1 on the 100 wash. */
    700: "#B7313B",
    800: "#93232C",
    900: "#6C171E",
    950: "#400D10",
};
/**
 * Mint — the live-status pair, and the only chromatic moment in light mode
 * other than the coral accent. 300 is Mint Edge (the pill's border and dot) and
 * 50 is Mint Wash (its fill); 700 is the label that sits on the wash at 5.71:1.
 *
 * It is deliberately a *different* ramp from `emerald`: emerald is the success
 * semantic (a payment cleared, a form saved) and mint is a system-health
 * indicator. Sharing one ramp would make «همه سرویس‌ها فعال» and «پرداخت شد»
 * indistinguishable at a glance, which is the opposite of what a status pill
 * is for.
 */
export const mint = {
    50: "#E4FBF1",
    100: "#CFF6E5",
    200: "#A9ECD0",
    300: "#7ED9B8",
    400: "#4FC49C",
    500: "#2BA97F",
    600: "#1E8A66",
    700: "#196E52",
    800: "#145440",
    900: "#0F3B2E",
    950: "#08221A",
};
/**
 * Slate — the dark canvas ladder and its foregrounds.
 *
 * Replaces `midnight`, whose every stop carried a violet cast to support a
 * violet CTA. That CTA is retired: the logo contains no violet, and the dark
 * variant of the mark is white-on-dark with the same coral petal. So the dark
 * ladder is now the light one's mirror — near-achromatic, with a trace of the
 * coral's own hue (C 0.006) so the canvas reads warm rather than clinical and
 * the accent sits *in* the surface instead of on top of it.
 *
 * 950 is the canvas. 800–850 are the raised surfaces; 600–700 carry the frost
 * edges. 100–300 are the foregrounds: `fg` at 100 is 16.49:1 on the canvas and
 * `fgSubtle` at 300 still holds 8.86:1, so the dark theme has more headroom
 * than the light one at every text role.
 */
export const slate = {
    /**
     * Cream — the editorial lift above paper (D7 reversed 2026-10-09). The
     * previous site's `--web-cream`, made a ramp stop so the web layer stays
     * made of the ramps. OKLCH L 0.973, C 0.0057, h 85°: an order of magnitude
     * under the raster gate's 0.04 neutral ceiling — warm paper, not a tint.
     */
    25: "#F8F6F2",
    50: "#F8F2F2",
    100: "#ECE6E6",
    200: "#D4CECE",
    300: "#AFAAAA",
    400: "#908B8A",
    500: "#5F5A5A",
    600: "#383434",
    700: "#312D2C",
    800: "#23201F",
    850: "#1B1817",
    900: "#131010",
    /** The dark canvas. */
    950: "#070505",
};
/**
 * Neutrals — ink, rule, and the greys between them.
 *
 * **925 is `#282828` exactly: the charcoal of the logo, unmodified.** It is the
 * body text, the structural hairline at full strength, and — since the re-skin
 * — the primary fill itself. Anchoring the ramp on the mark means `fg` and
 * `primary` are literally the same colour the logo is drawn in, rather than two
 * near-identical darks that differ by a tint nobody chose.
 *
 * The ramp is **achromatic**, which is a deliberate reversal. The previous
 * neutrals were warmed toward mauve to support a rose brand; with the brand
 * reduced to ink + coral, any tint in the greys fights the coral instead of
 * seating it. All the warmth in the system now comes from the accent, and the
 * greys stay out of its way.
 *
 * Being achromatic is also what makes the primary safe: `a11y.test.ts` separates
 * the destructive red from the primary, and an achromatic primary cannot be
 * confused with a red one at any hue distance.
 */
export const neutral = {
    0: "#FFFFFF",
    25: "#FAFAFA",
    50: "#F6F6F6",
    100: "#EEEEEE",
    150: "#E4E4E4",
    200: "#D9D9D9",
    300: "#BEBEBE",
    400: "#9E9E9E",
    450: "#909090",
    500: "#828282",
    550: "#767676",
    600: "#6A6A6A",
    700: "#545454",
    800: "#3F3F3F",
    850: "#343434",
    900: "#2D2D2D",
    /** Ink — the logo charcoal, exact. Body text, and the light-mode primary. */
    925: "#282828",
    950: "#1D1D1D",
    975: "#0B0B0B",
    1000: "#000000",
};
/** Success. A slightly cool emerald so it reads as "good" next to the coral. */
export const emerald = {
    50: "#EFFAF6",
    100: "#D9F2E8",
    200: "#B5E3D0",
    300: "#84CDB0",
    400: "#46B98D",
    500: "#259D71",
    600: "#18815C",
    700: "#106549",
    800: "#0E4E3A",
    900: "#0B372A",
    950: "#08211A",
};
/** Warning. Warm amber — the one hue that has to survive next to the coral without reading as a second brand colour. */
export const amber = {
    50: "#FEF8EC",
    100: "#FCEDCF",
    200: "#F7D69C",
    300: "#F2B964",
    400: "#F29C2C",
    // Darker than a "natural" 500 would land: this is the light-theme warning
    // dot, and #DF7C0C only reached 2.83:1 against the canvas.
    500: "#D36E09",
    600: "#AC550C",
    700: "#91440D",
    800: "#71320E",
    900: "#53250E",
    950: "#2F1409",
};
/**
 * Destructive. A deep, cool red — pushed to OKLCH hue 12° against the coral
 * accent's 21.5°, and darker at every stop.
 *
 * The separating axis changed again with the logo re-skin, and this time it had
 * to. The brand accent is now a warm coral at hue ≈0°, which is where a
 * destructive red naturally lives — the previous crimson sat 2–4° away and its
 * 50/100 washes were **ΔE 1.8** from the coral washes, i.e. an error banner and
 * a promotional banner were the same colour.
 *
 * Hue alone cannot fix that: rotating far enough to clear 20° would land the
 * danger ramp either back in the retired magenta or in amber's territory. So
 * the separation is **cooling plus lightness**: this ramp is bluer and darker
 * than the coral at every stop, which takes the washes to ΔE 6.0 / 8.9 and the
 * interactive stops to ΔE 16+.
 *
 * **400 is L 0.622 rather than the 0.598 an even ramp would give, and the
 * reason is a bug this ramp shipped with.** `colors.dark` pairs `danger:
 * crimson[400]` with `onDanger: neutral[975]`; at `#D24663` that measured
 * **4.4957:1** against the 4.5 floor. It went unnoticed because `a11y.test.ts`
 * compared `Number(ratio.toFixed(2))`, and 4.4957 rounds to 4.50 — the design
 * package certified a pair that `admin-panel` and `packages/ui` both rejected
 * on the raw float. The assertion now compares the unrounded ratio, and this
 * stop is `#DB4E6A`, which measures **4.98:1** with real margin while holding
 * ΔE 18.2 against `coral[400]`.
 *
 * The pair that actually matters for safety is not this one. A destructive
 * action must never be confused with the **primary** action, and since the
 * re-skin the primary is achromatic — ink in light, paper in dark. A 0%-
 * saturation fill cannot be mistaken for a 70%-saturation red at any hue
 * distance, which is a stronger guarantee than the old 25° arc ever gave.
 * `a11y.test.ts` asserts both properties.
 */
export const crimson = {
    50: "#FFE5E7",
    100: "#FFD0D5",
    200: "#FFA8B2",
    300: "#F1788A",
    /** Dark-mode `danger`. L 0.622, not 0.598 — see the note below. */
    400: "#DB4E6A",
    500: "#B62049",
    600: "#990037",
    700: "#7B002B",
    800: "#60001F",
    900: "#450014",
    950: "#260007",
};
/** Informational. A desaturated slate-blue that stays quiet against the coral. */
export const azure = {
    50: "#EFF5FB",
    100: "#D7E6F4",
    200: "#B1CBE7",
    300: "#80A7D0",
    400: "#5686BD",
    500: "#3A6DA6",
    600: "#2A578D",
    700: "#1F4475",
    800: "#1A355B",
    900: "#152742",
    950: "#0D1726",
};
export const palette = {
    coral,
    mint,
    slate,
    neutral,
    emerald,
    amber,
    crimson,
    azure,
};
export const colors = {
    light: {
        // Paper is flat white with no gradient and no texture; the coral wash
        // (`coral[50]`, 1.09:1 above paper) is the ONE lift above it, so `bgSubtle`
        // and `bgElevated` are the same colour on purpose — a card and an
        // alternating band are the same material, and the card is distinguished by
        // its rule and its hard shadow, not by a third tint.
        // `bgMuted`/`bgInset`/`bgHover`/`bgActive` deepen that same wash rather
        // than introducing grey, which is what keeps the one chromatic note in the
        // theme reading as warmth rather than as a second colour. The whole ladder
        // stays light enough that tertiary text clears 4.5:1 on all of it — the
        // tightest is `fgSubtle` on `bgInset` at 4.58:1.
        bg: neutral[0],
        bgSubtle: coral[50],
        bgMuted: "#F3EEEE",
        bgElevated: coral[50],
        bgInset: "#F0EBEB",
        bgHover: "#F9F5F5",
        bgActive: "#F2ECEC",
        fg: neutral[925],
        fgMuted: neutral[700],
        fgSubtle: neutral[600],
        // The primary IS ink now, so the colour that sits on it is paper, 14.74:1.
        // This token inverted with the re-skin: while the primary was pink, ink was
        // the only label that cleared 4.5:1 on it; on an ink ground that same value
        // is 1.00:1. It tracks `onPrimary` exactly and always has — see `fgOnInk`
        // for the case where the ground is ink but the fill is not the primary.
        fgOnEmphasis: neutral[0],
        // The inverted pair: paper on ink, 14.74:1. `fgMutedOnInk` is neutral[400]
        // at 5.50:1 — quiet enough to read as secondary, clear of 4.5:1.
        fgOnInk: neutral[0],
        fgMutedOnInk: neutral[400],
        fgDisabled: neutral[400],
        fgDisplay: neutral[975],
        // These two were INVERTED, and it is the largest single cause of "cards are
        // invisible in light mode". `border` is the default structural hairline —
        // it is what `.bk-surface` draws its rule with, and in Editorial Coral that
        // rule is the *only* thing separating a coral-washed card (#FFF2F2) from
        // the paper canvas (#FFFFFF), which are 1.09:1 apart. `borderSubtle` is the
        // quietest divider, for inside a dense table.
        //
        // They shipped as 150 / 200, i.e. the "quietest" divider was the DARKER of
        // the two and the default was the fainter: the card rule measured 1.25:1
        // against paper and 1.19:1 against the card itself, which is not a line, it
        // is a rumour. Swapping the roles and dropping one step gives the default
        // rule 1.86:1 against paper and 1.70:1 against the card, and leaves the
        // table divider at 1.41:1 — still quieter than the default, which is now
        // true rather than backwards.
        border: neutral[300],
        borderSubtle: neutral[200],
        // Ink, not a grey. Structural and interactive edges in Editorial Coral are
        // 1px solid ink — that hairline is most of the personality, and at 14.74:1
        // against paper it is also the strongest control outline the system has.
        borderStrong: neutral[925],
        primary: neutral[925],
        // **The hover ladder LIGHTENS in light mode.** It used to darken, and both
        // the direction and the reason inverted with the fill.
        //
        // Under the pink, the label was Ink and AntD's default lightening washed
        // the fill out against a white canvas until it lost the 3:1 UI-object
        // floor, so the ladder went down and was bounded by label contrast at
        // 4.63:1. There is no "down" left: the fill is already ink at 15.7%
        // lightness, and a hover the user cannot see is not a hover.
        //
        // Contrast stopped being the binding constraint entirely. Paper-on-fill and
        // fill-on-paper are the same number here (the canvas *is* the label), and
        // it runs 14.74:1 → 12.45:1 → 10.53:1 across the three steps; against a
        // washed card it is 13.50:1 → 11.40:1 → 9.65:1. Every step clears both
        // floors by an order of magnitude.
        //
        // What bounds the ladder now is identity, not legibility: 700 (#545454) is
        // still 7.57:1 but has stopped reading as ink and starts reading as a
        // disabled grey chip. Three steps inside the 925–800 band is as far as the
        // fill can move and still obviously be the same button.
        primaryHover: neutral[850],
        primaryActive: neutral[800],
        // The same ink as `fg`, which is the point rather than a shortcut: a
        // primary link in an achromatic system is distinguished by weight and
        // underline, not by hue. 14.74:1 on paper, 13.50:1 on a washed card, and
        // 12.71:1 on `primarySoft` — the case (a link inside a primary banner)
        // that forced the old ramp down to its 800 stop.
        primaryFg: neutral[925],
        primarySoft: neutral[100],
        primarySoftHover: neutral[150],
        primaryBorder: neutral[300],
        // Paper. The exact inverse of the fill, at the same 14.74:1.
        onPrimary: neutral[0],
        // Gold is retired and so is the pink that briefly replaced it. The accent
        // is the logo's coral, used as a drawn line and a tint — the restraint rule
        // is unchanged, only the hue is.
        //
        // 500 rather than 400 because this is the one accent stop that clears
        // §1.4.11's 3:1 as a graphical object: 3.36:1 on paper and 3.08:1 on a
        // washed card, against the logo coral's own 2.54:1. 700 is where the accent
        // becomes legible *as text* (5.97:1 on paper, 5.47:1 on the wash), which is
        // why `accentFg` and `accentStrong` are both there and `accent` is neither.
        accent: coral[500],
        accentStrong: coral[700],
        accentStrongHover: coral[800],
        accentFg: coral[700],
        // The same coral wash the surfaces are built from, so the premium chip is a
        // half-step quieter than the primary chip's neutral tint rather than
        // shouting over it.
        accentSoft: coral[50],
        accentBorder: coral[300],
        // Paper on coral[700] is 5.97:1; on the hover step, 8.34:1.
        onAccentStrong: neutral[0],
        // The mark: charcoal on a light ground, exactly as delivered.
        brandInk: neutral[925],
        brandCoral: coral[400],
        live: mint[300],
        liveFg: mint[700],
        liveSoft: mint[50],
        liveBorder: mint[300],
        success: emerald[600],
        successHover: emerald[700],
        successFg: emerald[700],
        successSoft: emerald[50],
        successBorder: emerald[200],
        onSuccess: neutral[0],
        warning: amber[500],
        warningStrong: amber[700],
        warningHover: amber[800],
        warningFg: amber[700],
        warningSoft: amber[50],
        warningBorder: amber[200],
        onWarning: neutral[0],
        danger: crimson[600],
        dangerHover: crimson[700],
        dangerFg: crimson[700],
        dangerSoft: crimson[50],
        dangerBorder: crimson[200],
        onDanger: neutral[0],
        info: azure[600],
        infoHover: azure[700],
        infoFg: azure[700],
        infoSoft: azure[50],
        infoBorder: azure[200],
        onInfo: neutral[0],
        // The ring is INK-GREY, not coral. It used to be coral[600] (#D4464D), and
        // on a focused-but-empty field — the autofocused phone input on /login is
        // the first thing a prospect sees — a 2px red edge reads as «this number
        // is invalid» before anything was typed. Coral at hue ≈0° is where the
        // destructive red lives (see the primary/danger note in `a11y.test.ts`);
        // the accent may sit there as ornament, but the one state every control
        // passes through cannot. neutral[700] is 7.5:1 on paper and clears 3:1 on
        // every surface of the ladder, and being achromatic it cannot be confused
        // with the error ring at any size or under any colour-vision deficiency.
        ring: neutral[700],
        overlay: "rgba(40, 40, 40, 0.42)",
        // Ink, as an RGB triplet. In light mode the shadow is not a tint at all —
        // it is a solid, blur-free offset of this exact colour.
        shadowColor: "40, 40, 40",
        skeleton: "#F0EBEB",
        skeletonHighlight: coral[50],
    },
    dark: {
        // Every surface above the canvas is painted as translucent glass; these are
        // the flattened composites (see `surfaceEffects.dark` for what is actually
        // drawn). Steel Plate is the top of the ladder and the ghost-button fill.
        bg: slate[950],
        bgSubtle: slate[900],
        bgMuted: slate[600],
        bgElevated: slate[850],
        // NOT slate[950]. It was, which made it byte-identical to `bg` — 1.00:1
        // — so every recessed surface vanished: a switch's unchecked track, a
        // slider rail, a progress remainder, `bk-numeric` wells. A recessed tone
        // cannot go far below a near-black canvas, so this sits one hair above it
        // and the shape is carried by the inset frost edge, as everything else in
        // Midnight Coral is.
        bgInset: "#0F0C0C",
        bgHover: slate[800],
        bgActive: slate[700],
        fg: slate[100],
        fgMuted: slate[200],
        fgSubtle: slate[300],
        // Dark's primary is paper, so its label is ink — the exact mirror of light,
        // and the same 14.74:1.
        fgOnEmphasis: neutral[925],
        // In dark, `fg` is a pale warm grey, so the inverted ground is a light
        // block and its text is near-black (16.49:1). A call site that hardcoded
        // white here — which looks right in light — would be invisible in dark.
        fgOnInk: slate[950],
        fgMutedOnInk: slate[500],
        fgDisabled: slate[500],
        fgDisplay: slate[50],
        // Never a solid stroke in dark — `border` is the flattened value of the
        // frost hairline, and components draw it as an INSET 1px shadow using
        // `--bk-glass-edge`. See `surfaceEffects.dark.edge`.
        border: "#2A2625",
        borderSubtle: "#1E1B1A",
        borderStrong: slate[400],
        // Paper, and the mirror of light in every particular: the fill is the
        // canvas's opposite (20.33:1 against it), the label is ink at 14.74:1, and
        // the hover ladder walks toward the ground rather than away from it —
        // 14.74:1 → 11.95:1 → 9.49:1 on the label, which is the same shape as
        // light's 14.74 → 12.45 → 10.53 read from the other end. AntD's default
        // lightens; here that would push a paper fill off the top of the ramp, so
        // dark darkens and light lightens. Both are "step toward the canvas", which
        // is the rule worth remembering — the direction is a consequence of it.
        primary: neutral[0],
        primaryHover: slate[100],
        primaryActive: slate[200],
        primaryFg: slate[50],
        primarySoft: slate[800],
        primarySoftHover: slate[700],
        primaryBorder: slate[600],
        onPrimary: neutral[925],
        // In dark the accent is the logo coral at full strength, and it is
        // atmosphere only — halos, focus punctuation, the rule under an active tab.
        // It is never a button fill here, and since the primary went achromatic
        // there is now NO chromatic fill in Midnight Coral at all: the whole theme
        // is greys plus the status roles plus this one warm note.
        //
        // 400 rather than 500 because a dark ground reverses the problem light has
        // — the logo coral is 7.99:1 against the canvas here, so the stop that was
        // too faint to be a UI object on paper is comfortably the strongest choice
        // on ink.
        accent: coral[400],
        accentStrong: coral[400],
        accentStrongHover: coral[300],
        accentFg: coral[300],
        accentSoft: "#2A1616",
        accentBorder: coral[900],
        // Ink on the coral, 5.80:1. Paper on it is 2.54:1 in either theme and is
        // never correct — which is the reason the light theme reaches for coral[700]
        // to carry a label and this one does not have to.
        onAccentStrong: neutral[925],
        // The mark inverts: paper ink on a dark ground. The coral does not.
        brandInk: neutral[0],
        brandCoral: coral[400],
        live: mint[300],
        liveFg: mint[300],
        liveSoft: "#0D2019",
        liveBorder: mint[800],
        success: emerald[400],
        successHover: emerald[300],
        successFg: emerald[300],
        successSoft: emerald[950],
        successBorder: emerald[800],
        onSuccess: neutral[975],
        warning: amber[400],
        warningStrong: amber[400],
        warningHover: amber[300],
        warningFg: amber[300],
        warningSoft: amber[950],
        warningBorder: amber[800],
        onWarning: neutral[975],
        danger: crimson[400],
        dangerHover: crimson[300],
        dangerFg: crimson[300],
        dangerSoft: crimson[950],
        dangerBorder: crimson[800],
        // 4.9768:1 on `danger`, and the margin is the point. This pair measured
        // 4.4957:1 while `crimson[400]` was `#D24663` — a fail that the suite
        // reported as a pass, because the assertion rounded the ratio to two
        // decimals before comparing it. The fix was to the ramp (see `crimson`) and
        // to the assertion, not to this token: `neutral[975]` is the shared dark
        // on-colour and `onSuccess`/`onWarning`/`onInfo` all use it too, so moving
        // it here to buy contrast would have split one role into two values to
        // paper over a stop that was simply too light.
        onDanger: neutral[975],
        info: azure[400],
        infoHover: azure[300],
        infoFg: azure[300],
        infoSoft: azure[950],
        infoBorder: azure[800],
        onInfo: neutral[975],
        // Paper-grey, for the same reason as light: a coral ring is a red edge on
        // every focused field. neutral[300] clears 3:1 on every dark surface, and
        // because the ring is drawn at a 2px offset it sits on the surface behind
        // a focused paper-white primary button rather than on its fill.
        ring: neutral[300],
        overlay: "rgba(7, 5, 5, 0.72)",
        shadowColor: "7, 5, 5",
        skeleton: slate[800],
        skeletonHighlight: slate[700],
    },
};
export const surfaceEffects = {
    light: {
        glass: coral[50],
        glassStrong: neutral[0],
        // Follows `colors.light.border` — the universal hairline is one value, and
        // a second spelling of it is how a design system starts to rot.
        edge: neutral[300],
        luminous: coral[100],
        // The blueprint grid stays at the old, fainter step. It is texture behind
        // content, not structure, and it is invisible in light mode anyway.
        gridline: neutral[150],
        gridSize: "80px",
        displayFill: neutral[975],
        // Exactly one gradient in the whole light theme. Soft, wide, and centred
        // behind the hero headline; it never touches a card or a body background.
        //
        // `249, 125, 125` is `coral[400]` — the mark's own colour, not the accent
        // step. The accent (`coral[500]`) is tuned to clear 3:1 as a drawn object;
        // a 22%-alpha wash is neither drawn nor an object, and the hero is the one
        // place the brand should be speaking in its literal voice. The outer stop
        // names the same colour at alpha 0 rather than `transparent` so no engine
        // can interpolate the fade through grey.
        heroHalo: "radial-gradient(62% 52% at 50% 38%, rgba(249, 125, 125, 0.22) 0%, rgba(249, 125, 125, 0.08) 42%, rgba(249, 125, 125, 0) 72%)",
    },
    dark: {
        // `212, 206, 206` is `slate[200]`: the frost is the canvas ladder's own
        // pale end, so a glass surface is the same material as the wall behind it.
        // Every alpha here is unchanged from the pre-re-skin values — the material
        // kept its weight and lost its violet cast, which was the whole edit.
        glass: "rgba(212, 206, 206, 0.03)",
        // `7, 5, 5` is `slate[950]`, the canvas. A modal is the canvas at 97%, so
        // this has to track `bg`; it was still the OLD canvas (`7, 6, 15`), which
        // is how a violet-black modal survived on a warm-black page.
        glassStrong: "rgba(7, 5, 5, 0.97)",
        // `colors.dark.border` (#2A2625) is documented as the flattened value of
        // this hairline. It is now at least the same *material*: 12% of slate[200]
        // over the canvas composites to **#201D1D**, a warm grey, where the lavender
        // wash resolved to #201C22 — violet-cast, i.e. disagreeing with the token in
        // hue as well as in value. A value gap remains (#201D1D painted vs #2A2625
        // declared) and it is not closed here, because closing it means deciding
        // which of the two is the source and re-deriving the other — a change to a
        // contract-listed token, not to a wash. Both are decorative and exempt from
        // the contrast contract, so nothing is mis-scored today; see the note on
        // `SurfaceEffects` about giving this layer an assertion so it cannot drift
        // again unnoticed.
        edge: "rgba(212, 206, 206, 0.12)",
        luminous: "rgba(212, 206, 206, 0.10)",
        gridline: "rgba(212, 206, 206, 0.06)",
        gridSize: "80px",
        // The display gradient, re-derived as the mark's two colours: `slate[50]`
        // at the baseline rising into `coral[400]`. It was `#F2E7F5 → #B39BEF`, a
        // violet — the last one in the system, and the most visible, since this is
        // the marketing H1 in dark. Structure is unchanged (one pale stop, one
        // chromatic stop, bottom to top) and both stops clear large-text contrast
        // on the canvas: 18.37:1 and 7.99:1.
        displayFill: "linear-gradient(0deg, #F8F2F2 0%, #F97D7D 100%)",
        // One accent, so one hue. The old halo was violet into pink because the old
        // dark theme had two chromatic tokens — a violet CTA and a pink accent — and
        // the gradient was showing both. There is exactly one now, and the primary
        // is achromatic, so a second note would be inventing a colour the system
        // does not have. The two stops stay (0.28 core, 0.10 mid) so the falloff
        // keeps its shape; the outer stop is the new canvas at alpha 0.
        heroHalo: "radial-gradient(62% 52% at 50% 38%, rgba(249, 125, 125, 0.28) 0%, rgba(249, 125, 125, 0.10) 44%, rgba(7, 5, 5, 0) 72%)",
    },
};
/* ------------------------------------------------------------------ */
/*  Spacing                                                            */
/* ------------------------------------------------------------------ */
/**
 * A 4px base grid. Keys are the multiplier (so `4` is 16px), with `px`, `0.5`
 * and `1.5` for the sub-grid values that dense clinical tables need.
 */
export const spacing = {
    0: "0px",
    px: "1px",
    0.5: "2px",
    1: "4px",
    1.5: "6px",
    2: "8px",
    2.5: "10px",
    3: "12px",
    3.5: "14px",
    4: "16px",
    5: "20px",
    6: "24px",
    7: "28px",
    8: "32px",
    9: "36px",
    10: "40px",
    11: "44px",
    12: "48px",
    14: "56px",
    16: "64px",
    20: "80px",
    24: "96px",
    28: "112px",
    32: "128px",
    40: "160px",
    48: "192px",
    56: "224px",
    64: "256px",
};
/**
 * Vertical rhythm between marketing-site sections (fluid), widened for the
 * re-skin: the light rhythm lands on 80px at the target width and the loose
 * one on 120px, which is the dark theme's slower cadence.
 *
 * **Marketing only.** The panels keep their own container and the AntD theme
 * never imports either of these — a dense clinical table does not get an 80px
 * section gap.
 */
export const sectionSpacing = "clamp(3rem, 2rem + 5vw, 5rem)";
export const sectionSpacingLoose = "clamp(4rem, 2.5rem + 7vw, 7.5rem)";
/** Card padding and the element gap inside one, per personality. */
export const density = {
    light: { cardPadding: spacing[6], gap: spacing[2] },
    dark: { cardPadding: spacing[6], gap: spacing[4] },
};
/* ------------------------------------------------------------------ */
/*  Radii                                                              */
/* ------------------------------------------------------------------ */
/**
 * The primitive radius scale. Mode-independent, because it is a scale, not a
 * decision — the decision is `semanticRadii` below.
 */
export const radii = {
    none: "0px",
    xs: "4px",
    sm: "6px",
    md: "8px",
    lg: "10px",
    xl: "12px",
    "2xl": "16px",
    "3xl": "20px",
    "4xl": "28px",
    pill: "999px",
    full: "9999px",
};
export const semanticRadii = {
    light: {
        control: radii.xs,
        controlBlock: radii.none,
        input: radii.xs,
        card: radii.md,
        modal: radii.md,
        badge: radii.full,
        icon: radii.md,
        pill: radii.full,
    },
    dark: {
        control: radii.pill,
        controlBlock: radii.pill,
        input: radii.sm,
        card: radii["2xl"],
        modal: radii["2xl"],
        badge: radii.sm,
        icon: radii.full,
        pill: radii.full,
    },
};
/**
 * **Light: hard offsets. There is no blurred drop-shadow anywhere in light
 * mode.** Every step is `<x> <y> 0 0 <ink>` — a solid, unblurred plate of the
 * same ink the borders are drawn in. That single decision is most of what makes
 * the light theme read as print rather than as a 2015 material app, and
 * `a11y.test.ts` asserts the blur radius of every light token is zero so it
 * cannot quietly regress.
 *
 * The x offset is `calc(var(--bk-elev-sign) * Npx)`, not a literal. A hard
 * shadow has a direction, and in an RTL document it must fall to the
 * *inline*-end, i.e. to the left. CSS has no logical box-shadow, so the sign
 * lives in a custom property that the base layer flips on `[dir="rtl"]`. A sign
 * rather than a length, because the scale runs 1/2/3/4/6px and each step keeps
 * its own magnitude. Consumers that need the raw number (a canvas, a PDF) read
 * `shadowOffset`.
 *
 * **Dark: inset frost plus an outer halo, never a conventional drop shadow.**
 * A charcoal card lifted by a black shadow just looks dirty; a glass card is
 * lifted by the light caught on its top edge. Every step therefore leads with
 * an inset highlight, and the outer component is a wide, very soft halo of the
 * canvas colour rather than of black.
 */
export const shadowOffset = "2px";
export const shadows = {
    light: {
        none: "none",
        // The plate is `neutral[925]` — the same ink `colors.light.fg` and the
        // structural borders are drawn in, and the mark's own charcoal. Referenced,
        // not spelled: these five used to carry the literal `#282828` five lines
        // above the `inner` note that argues the exact opposite about `neutral[150]`
        // — *"referenced rather than spelled: it was frozen as the literal
        // `#EDE4E7` … and became a stale tint the moment the neutrals were
        // re-derived"*. Same trap, five lines up, and `a11y.test.ts` only asserted
        // that `sm` contains `colors.light.fg`, so the other four could drift.
        xs: `calc(var(--bk-elev-sign, 1) * 1px) 1px 0 0 ${neutral[925]}`,
        sm: `calc(var(--bk-elev-sign, 1) * 2px) 2px 0 0 ${neutral[925]}`,
        md: `calc(var(--bk-elev-sign, 1) * 3px) 3px 0 0 ${neutral[925]}`,
        lg: `calc(var(--bk-elev-sign, 1) * 4px) 4px 0 0 ${neutral[925]}`,
        xl: `calc(var(--bk-elev-sign, 1) * 6px) 6px 0 0 ${neutral[925]}`,
        // `neutral[150]`, referenced rather than spelled, for the same reason.
        inner: `inset calc(var(--bk-elev-sign, 1) * 2px) 2px 0 0 ${neutral[150]}`,
        glow: "none",
        // The box-shadow form of the canonical focus ring, for controls that
        // cannot use `outline` (AntD paints its focus as a box-shadow). SOLID, not
        // a translucent glow — see the focus note on `control` for the measured
        // reason. It is flush rather than offset because a box-shadow spread has no
        // offset parameter; the offset version is the `outline` in the base layer.
        focus: `0 0 0 2px ${colors.light.ring}`,
    },
    dark: {
        none: "none",
        // Three channel triplets and nothing else, all taken from the ramps rather
        // than eyeballed, with **every alpha unchanged** from the pre-re-skin stack
        // so the elevation keeps its exact weight:
        //
        //   212, 206, 206  slate[200]  — the frost highlight (was #CFCADF, midnight[200])
        //   248, 242, 242  slate[50]   — the brighter highlight (was #F2E7F5, midnight[50])
        //   249, 125, 125  coral[400]  — the one chromatic note (was #B39BEF, violet[350])
        //     7,   5,   5  slate[950]  — the canvas, i.e. `shadowColor` (was 6, 6, 14)
        //
        // `rgba(0, 0, 0, …)` stays literal black: it is the absence of light under a
        // floating panel, not a palette colour, and it never belonged to a ramp.
        xs: "inset 0 1px 1px rgba(212, 206, 206, 0.10)",
        sm: "inset 0 1px 1px rgba(212, 206, 206, 0.12), 0 8px 16px rgba(7, 5, 5, 0.5)",
        // The brief's card recipe, re-channelled.
        md: "inset 0 1px 1px rgba(212, 206, 206, 0.12), inset 0 24px 48px rgba(212, 206, 206, 0.05), 0 24px 32px rgba(7, 5, 5, 0.7)",
        // The brief's modal / floating-card recipe, re-channelled.
        lg: "inset 0 1px 1px rgba(248, 242, 242, 0.2), inset 0 24px 48px rgba(249, 125, 125, 0.06), 0 16px 32px rgba(0, 0, 0, 0.3)",
        xl: "inset 0 1px 1px rgba(248, 242, 242, 0.2), inset 0 24px 48px rgba(249, 125, 125, 0.06), 0 32px 64px rgba(0, 0, 0, 0.45)",
        inner: "inset 0 1px 0 0 rgba(212, 206, 206, 0.12)",
        // **Coral throughout, at both radii.** It was violet at 6px inside pink at
        // 12px — a two-hue glow, which existed because the old dark theme had two
        // chromatic tokens to show off: a violet CTA and a pink accent. Neither
        // survives. The dark primary is now paper, and a paper glow around a
        // paper-white fill is invisible, so a "core from the primary" reading is not
        // available either. That leaves one hue, and it is the right one: `accent`'s
        // own docstring already assigns halos to the coral. Two radii at the
        // original alphas keep the falloff a falloff rather than a flat ring.
        glow: "0 0 6px rgba(249, 125, 125, 0.32), 0 0 12px rgba(249, 125, 125, 0.2)",
        focus: `0 0 0 2px ${colors.dark.ring}`,
    },
};
/* ------------------------------------------------------------------ */
/*  Typography                                                         */
/* ------------------------------------------------------------------ */
export const fontFamily = {
    /** Kalameh — display and headings. */
    display: '"Kalameh", "Yekan Bakh", "Vazirmatn", Tahoma, system-ui, sans-serif',
    /** Yekan Bakh — body, UI, forms, tables. */
    body: '"Yekan Bakh", "Kalameh", "Vazirmatn", Tahoma, system-ui, sans-serif',
    /**
     * Figures that must line up: money columns, quantities, invoice totals.
     *
     * This is the body stack **without Kalameh**, and that omission is the whole
     * point. Kalameh has no `tnum` — verified against the shipped binaries and
     * against the vendor TTFs, so it is absent from the family, not lost in
     * conversion — and its digits are drawn at ten different advance widths
     * (۰=447, ۱=216, ۵=699 units in Regular). A price rendered in Kalameh cannot
     * be made tabular by any CSS. Yekan Bakh's `tnum` maps all ten Persian digits
     * to a uniform 700-unit advance at every weight, so numerals are pinned to
     * Yekan Bakh regardless of what the surrounding heading is set in.
     */
    numeric: '"Yekan Bakh", "Vazirmatn", Tahoma, system-ui, sans-serif',
    /** Latin-only contexts: SKUs, IDs, batch numbers. */
    mono: '"JetBrains Mono", ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
};
/**
 * The weight ladder, mapped onto the eight cuts that actually ship.
 *
 * Kalameh (Eco) draws 100 / 400 / 700 / 900; Yekan Bakh draws 300 / 400 / 600 /
 * 700. Neither family has a 500. The `@font-face` blocks in `fonts.css` declare
 * weight *ranges* so every step below lands on a real cut instead of silently
 * resolving down — `medium` (500) reaches Yekan Bakh SemiBold in body text and
 * Kalameh Bold in a heading, rather than rendering identically to `regular`.
 *
 * `black` is 900, not 800: Kalameh Black is the heaviest cut in the Eco package
 * and 900 is where it is drawn. It is the weight of the **one emphasised word**
 * in a headline — the re-skin's signature is that there is never a second one.
 */
export const fontWeight = {
    light: 300,
    regular: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
    black: 900,
};
/**
 * The type scale. The re-skin's ladder is **14 / 16 / 18 / 20 / 28 / 36 / 48 /
 * 56**; the three sub-14 steps (13/12/11) are panel furniture — table
 * captions, tag labels, timestamps — and are outside the marketing scale on
 * purpose.
 *
 * Three rules are non-negotiable for Persian, and two of them override the
 * reference decks the re-skin is drawn from:
 *
 * 1. **Line height clears the font's own content box.** This is measured, not
 *    guessed, and `fonts.test.ts` re-asserts it against the shipped binaries.
 *    Both families set `USE_TYPO_METRICS`, so Chrome and Firefox build the line
 *    box from OS/2 typo metrics:
 *
 *    | family     | typo asc/desc | content box | hhea (Safari/macOS) |
 *    |------------|---------------|-------------|---------------------|
 *    | Kalameh    | 1000 / −500   | **1.50em**  | 1200 / −500 = 1.70em |
 *    | Yekan Bakh | 1000 / −550   | **1.55em**  | 1000 / −550 = 1.55em |
 *
 *    The brief asks for `1.25` on display, itself already looser than the Latin
 *    reference's `1.16`. **Both are under Kalameh's own box**, and a
 *    `line-height` below the box makes the line smaller than the one the
 *    designer drew into: ascenders clip under `overflow: hidden`, and
 *    consecutive lines collide where a ی from one meets a ب from the next. The
 *    display steps therefore sit at 1.5 — the measured floor, and the loosest
 *    reading of the brief's own instruction to go looser than the reference.
 *    Body is 1.7, exactly as the brief asks and comfortably clear of 1.55.
 *
 * 2. **Letter spacing is always `0`, in every step, with no exception.** The
 *    brief specifies −0.005em to −0.02em display tracking and a +0.08em eyebrow,
 *    both taken from Latin decks. Arabic-script glyphs *join*: negative tracking
 *    pulls the joins into each other and positive tracking pulls them apart, so
 *    the word renders as a row of disconnected shapes. It is not a taste call
 *    and `fonts.test.ts` enforces it. The eyebrow's quietness comes from size,
 *    weight and colour instead — Yekan Bakh Medium at `body`, `fgSubtle`, with
 *    optional fading hairlines either side (`.bk-eyebrow` in `css-vars.ts`).
 *    Positive tracking stays reserved for Latin-only meta strings, set at the
 *    call site on the `mono` family.
 * 3. **Numerals are not the display family.** See `fontFamily.numeric`.
 */
export const typeScale = {
    "display-xl": {
        fontSize: "3.5rem",
        lineHeight: "1.5",
        fontWeight: fontWeight.bold,
        letterSpacing: "0",
        fontFamily: fontFamily.display,
    },
    "display-lg": {
        fontSize: "3rem",
        lineHeight: "1.5",
        fontWeight: fontWeight.bold,
        letterSpacing: "0",
        fontFamily: fontFamily.display,
    },
    display: {
        fontSize: "2.25rem",
        lineHeight: "1.5",
        fontWeight: fontWeight.bold,
        letterSpacing: "0",
        fontFamily: fontFamily.display,
    },
    "heading-lg": {
        fontSize: "1.75rem",
        lineHeight: "1.5",
        fontWeight: fontWeight.bold,
        letterSpacing: "0",
        fontFamily: fontFamily.display,
    },
    heading: {
        fontSize: "1.25rem",
        lineHeight: "1.5",
        fontWeight: fontWeight.semibold,
        letterSpacing: "0",
        fontFamily: fontFamily.display,
    },
    "heading-sm": {
        fontSize: "1.125rem",
        lineHeight: "1.55",
        fontWeight: fontWeight.semibold,
        letterSpacing: "0",
        fontFamily: fontFamily.display,
    },
    title: {
        fontSize: "1rem",
        lineHeight: "1.6",
        fontWeight: fontWeight.semibold,
        letterSpacing: "0",
        fontFamily: fontFamily.display,
    },
    "title-sm": {
        fontSize: "1rem",
        lineHeight: "1.6",
        fontWeight: fontWeight.semibold,
        letterSpacing: "0",
        fontFamily: fontFamily.body,
    },
    "body-lg": {
        fontSize: "1rem",
        lineHeight: "1.7",
        fontWeight: fontWeight.regular,
        letterSpacing: "0",
        fontFamily: fontFamily.body,
    },
    body: {
        fontSize: "0.875rem",
        lineHeight: "1.7",
        fontWeight: fontWeight.regular,
        letterSpacing: "0",
        fontFamily: fontFamily.body,
    },
    "body-sm": {
        fontSize: "0.8125rem",
        lineHeight: "1.7",
        fontWeight: fontWeight.regular,
        letterSpacing: "0",
        fontFamily: fontFamily.body,
    },
    caption: {
        fontSize: "0.75rem",
        lineHeight: "1.65",
        fontWeight: fontWeight.regular,
        letterSpacing: "0",
        fontFamily: fontFamily.body,
    },
    "caption-sm": {
        fontSize: "0.6875rem",
        lineHeight: "1.6",
        fontWeight: fontWeight.medium,
        letterSpacing: "0",
        fontFamily: fontFamily.body,
    },
};
/**
 * Numeral rendering. Prices, unit counts and batch numbers must be tabular so
 * columns of تومان figures line up.
 *
 * ## Only features the shipped fonts actually have
 *
 * Enumerated with fontTools against the eight woff2 in `../fonts/`:
 *
 * - **Yekan Bakh** — `tnum`, `zero`, `frac`, `sups`, `subs`, `sinf`, `ordn`,
 *   `case`, `salt`, `ss01`–`ss07`, `swsh`, plus the shaping set.
 * - **Kalameh** — `dlig`, `calt`, `salt`, `ss01`, `ss02`, plus the shaping set.
 *   **No `tnum`.** Neither has `lnum`, `onum` or `pnum`.
 *
 * `lnum` and `pnum` were being requested and silently did nothing; they are
 * gone. All figures in both families are lining by default, and proportional is
 * the default state, so "proportional" is expressed as `normal` rather than by
 * asking for a feature that does not exist.
 *
 * ## Why `ss01` is NOT switched on
 *
 * It is the FaNum trap wearing a different hat. In **Yekan Bakh**, `ss01`
 * substitutes U+0030–0039 → U+06F0–06F9 — exactly what a FaNum cut does, only
 * at feature level. Bookora converts digits in data (`toPersianDigits` in
 * `@bookora/shared`), so turning `ss01` on would additionally Persian-ise every
 * string that must stay Latin: clinic slugs, IBAN/شبا, batch and card numbers,
 * the `98` prefix, coordinates. In **Kalameh** the same tag means something
 * else entirely — a ک/گ swash alternate — so there is no setting of `ss01` that
 * is correct for both families at once.
 *
 * ## The tabular fallback
 *
 * `tnum` only exists in Yekan Bakh. That is why `.bk-numeric` sets
 * `font-family: var(--bk-font-numeric)` as well as the feature: a figure inside
 * a Kalameh heading is re-homed to Yekan Bakh so it can be tabular at all,
 * instead of jittering a table column by up to 480 font units per digit.
 */
export const fontFeature = {
    /** Aligned figures — every money and quantity cell in the panels. */
    tabular: '"tnum" 1',
    /** Running text — the default; proportional figures read better inline. */
    proportional: "normal",
};
export const typography = {
    fontFamily,
    fontWeight,
    scale: typeScale,
    feature: fontFeature,
};
/* ------------------------------------------------------------------ */
/*  Motion                                                             */
/* ------------------------------------------------------------------ */
/**
 * 120–220ms, ease-out. Long enough to be read as motion, short enough that a
 * receptionist clicking through forty patients an hour never waits on it.
 * Every consumer wraps these in `@media (prefers-reduced-motion: reduce)`.
 *
 * Two registers share this one scale:
 *
 *   - **Staff panels** live on `fast` / `base` / `slow` (120–220ms). Nothing a
 *     receptionist triggers may take longer than `slow` to settle.
 *   - **The public site and the patient app** may also reach for `slower` and
 *     `deliberate` — a section reveal, a hero entrance, a success moment.
 *
 * Exits run at roughly three quarters of their entrance: an element that is
 * leaving has already been understood, so a modal that opened on `slow` closes
 * on `base`, and a toast that entered on `base` leaves on `fast`.
 */
export const duration = {
    instant: "0ms",
    /** Press feedback, hover, toggles, colour changes. */
    fast: "120ms",
    /** Default: popovers, menus, tooltips, a content swap in place. */
    base: "160ms",
    /** Larger surfaces: modal, drawer, a panel entering the page. */
    slow: "220ms",
    /** Expressive surfaces only: a card or list settling into view. */
    slower: "320ms",
    /** Expressive surfaces only: hero choreography, a scroll reveal. Never in a panel. */
    deliberate: "480ms",
    /** Skeleton shimmer and other ambient loops. */
    ambient: "1600ms",
};
export const easing = {
    /** Default for anything entering or settling (quint out). */
    out: "cubic-bezier(0.22, 1, 0.36, 1)",
    /**
     * A stronger deceleration (expo out) for a large surface arriving from a
     * distance: a drawer, a sheet, a hero block. It covers most of the travel in
     * the first third, so a big move still reads as responsive.
     */
    emphasized: "cubic-bezier(0.16, 1, 0.3, 1)",
    /** Anything leaving the screen. */
    in: "cubic-bezier(0.4, 0, 1, 1)",
    /** Position/size changes that both start and end on screen. */
    inOut: "cubic-bezier(0.65, 0, 0.35, 1)",
    /** Neutral ramp for colour and opacity crossfades. */
    standard: "cubic-bezier(0.2, 0, 0.2, 1)",
    /**
     * A spring-like settle for the expressive surfaces: it passes the target by
     * about two percent and comes back, which the eye reads as "landed" rather
     * than "bounced". For a card or an illustration settling into place on the
     * public site or in the patient app. Never in a staff panel, and never on a
     * colour or opacity crossfade.
     */
    gentle: "cubic-bezier(0.32, 1.12, 0.52, 1)",
    /**
     * The press-and-settle spring: a visible overshoot past the target and back.
     * Reserved for a primary action confirming itself in the patient app (a
     * booking button, a success mark popping in). Never in a staff panel, never
     * for a colour or opacity crossfade, where an overshoot reads as a flicker.
     *
     * A cubic-bézier, not a physical spring, because it has to be usable from a
     * CSS `transition` in the Astro site as well as from `motion/react` in the
     * PWA, and it must be expressible as a single CSS custom property. The y1 of
     * 1.56 is what produces the overshoot (roughly ten percent of the travel);
     * `gentle` above is the quiet version and the one to reach for first.
     */
    spring: "cubic-bezier(0.34, 1.56, 0.42, 1)",
    linear: "linear",
};
/**
 * How far things travel. Motion in a clinic product suggests direction; it
 * does not fly content across the screen. `xs`/`sm` are the panel range,
 * `md`–`xl` belong to the expressive surfaces.
 *
 * Horizontal travel is always written against the inline axis and multiplied
 * by `--bk-inline-sign` (1 in LTR, -1 in RTL) so "forward" is right-to-left in
 * a Persian document without a second rule.
 */
export const shift = {
    /** A pressed control sinking into its plate. */
    xs: "2px",
    /** Panel content entering: a popover, a row, a card's body. */
    sm: "6px",
    /** A patient-app card or a toast. */
    md: "12px",
    /** A section revealed on scroll on the public site. */
    lg: "24px",
    /** Hero choreography. */
    xl: "40px",
};
/** Scale factors. Two, deliberately: more would invite zoom effects. */
export const motionScale = {
    /** The start of a scale-in (popover, modal, menu). */
    enter: "0.97",
    /** A pressed control. */
    press: "0.98",
};
/**
 * The delay between siblings in a staggered entrance. Stagger is capped at six
 * items wherever it is applied, so a long list never makes the last row wait
 * more than a quarter of a second.
 */
export const stagger = "40ms";
export const motion = {
    duration,
    easing,
    shift,
    scale: motionScale,
    stagger,
};
/* ------------------------------------------------------------------ */
/*  Layers                                                             */
/* ------------------------------------------------------------------ */
/**
 * Stacking order. AntD's `zIndexPopupBase` is pinned to `modal` in
 * `antd-theme.ts` so AntD's own popup family lands inside this scale instead of
 * fighting it.
 */
export const zIndex = {
    hide: -1,
    base: 0,
    raised: 10,
    sticky: 100,
    header: 200,
    sidebar: 300,
    overlay: 1000,
    drawer: 1100,
    modal: 1200,
    popover: 1300,
    tooltip: 1400,
    /**
     * The tour's coachmark (spotlight, blocker and panel). Derived from antd
     * 6.6.5's real stacking arithmetic, not from these tokens, because antd only
     * reads `zIndexPopupBase` (= `modal`) and adds to it:
     *
     *   Drawer, Modal (top level) ....................................... 1200
     *   Popover / Select / Tooltip (top level) .............. 1230 / 1250 / 1270
     *   Container nested in a Drawer or Modal (1200 + 100) ............. 1300
     *   Select / Dropdown inside a Drawer (1300 + 50) .................. 1350
     *   Tooltip or nested Modal inside a Drawer (1300 + 100) ........... 1400
     *   → coachmark ..................................................... 1450
     *   toast (this scale) .............................................. 1500
     *   impersonation banner ............................................ 1800
     *   antd message / notification (base + 1000 + 10 / 50) ....... 2210 / 2250
     *
     * So a floating tour step covers everything a Drawer can nest (the session
     * form's popups included), and stays under the impersonation banner and
     * antd's messages. Tooltips are suppressed while a modal-ish step is open
     * rather than raised above it. `coachmark-z-order.test.tsx` renders the real
     * components and fails if an antd upgrade changes this arithmetic.
     */
    coachmark: 1450,
    toast: 1500,
    /** The impersonation banner must sit above everything the panel can open. */
    impersonationBanner: 1800,
};
/* ------------------------------------------------------------------ */
/*  Breakpoints                                                        */
/* ------------------------------------------------------------------ */
/**
 * One set of breakpoints for all four surfaces. These are AntD v5's grid
 * breakpoints (the panels are the constraint), and the Tailwind preset
 * overrides Tailwind's defaults with them so a `md:` in the patient PWA and a
 * `md` in an AntD `<Col>` mean the same width.
 */
export const breakpoints = {
    xs: "480px",
    sm: "576px",
    md: "768px",
    lg: "992px",
    xl: "1200px",
    "2xl": "1440px",
};
/**
 * Max content width. `xl` is 1200px and that is the marketing page width the
 * brief names; the panels use their own shell and do not read this.
 */
export const containerMaxWidth = {
    sm: "640px",
    md: "768px",
    lg: "1024px",
    xl: "1200px",
    "2xl": "1320px",
    prose: "68ch",
};
/* ------------------------------------------------------------------ */
/*  Component metrics                                                  */
/* ------------------------------------------------------------------ */
/**
 * ════════════════════════════════════════════════════════════════════════════
 *  THE CANONICAL CONTROL SIZE SCALE
 * ════════════════════════════════════════════════════════════════════════════
 *
 * **This is the single source of geometry for every input, select, textarea,
 * button, chip and segmented control on all four surfaces.** An input in the
 * Astro site, an input in the patient PWA and an AntD `<Input>` in either panel
 * are the same object at the same size, and the only way that stays true is
 * that none of them owns a number.
 *
 * It exists because three scales had already grown apart and nothing failed:
 * `control.paddingInline` said 8/12/16, `buttonStyles` said `px-3/px-4/px-5`
 * (12/16/20) and `inputStyles` said `px-2.5/px-3/px-3` (10/12/12). All three
 * were "the" padding. Font size was worse — it was not a token at all, so the
 * `lg` field ran at `text-body` (14px) under a comment claiming it was 16px to
 * avoid iOS zoom-on-focus. It was not, and iOS zoomed.
 *
 * Three steps, and only three:
 *
 * | step | height | padding-inline | font-size        | icon | gap |
 * |------|--------|----------------|------------------|------|-----|
 * | `sm` | 28px   | 10px           | 13px (`body-sm`) | 14px | 6px |
 * | `md` | 36px   | 12px           | 14px (`body`)    | 16px | 8px |
 * | `lg` | 44px   | 16px           | 16px (`body-lg`) | 18px | 8px |
 *
 * - `md` (36px, not AntD's 32px) is the panel default — these are tools a
 *   receptionist lives in for eight hours, on a reception-desk tablet.
 * - `lg` (44px) is the patient PWA default and the marketing site's primary
 *   action. 44px is the WCAG 2.1 target-size floor, and **16px is the floor at
 *   which mobile Safari stops zooming the viewport on focus** — which is why
 *   `fontSize.lg` is a token and not a call-site choice.
 * - `sm` is panel furniture: a table row action, a filter bar control.
 *
 * The radius is deliberately NOT here: it is `semanticRadii.input` /
 * `.control`, because it inverts between the two personalities and the size
 * step has nothing to do with it.
 *
 * ── Focus ───────────────────────────────────────────────────────────────────
 *
 * **On focus, exactly one thing happens: a ring appears. The border colour
 * does not change.** See `focusRing` below.
 */
export const control = {
    height: { sm: "28px", md: "36px", lg: "44px" },
    paddingInline: { sm: "10px", md: spacing[3], lg: spacing[4] },
    /**
     * The label size inside a control. `lg` is 16px because anything smaller
     * makes mobile Safari zoom the viewport when the field takes focus.
     */
    fontSize: { sm: "0.8125rem", md: "0.875rem", lg: "1rem" },
    /** Gap between a control's icon and its label. */
    gap: { sm: spacing[1.5], md: spacing[2], lg: spacing[2] },
    /** Leading/trailing icon size inside a control. */
    iconSize: { sm: "14px", md: "16px", lg: "18px" },
    /**
     * ── THE CANONICAL FOCUS TREATMENT ─────────────────────────────────────────
     *
     * **One indicator. A ring, on `:focus-visible`, and nothing else changes.**
     *
     * The border colour does **not** change on focus. Hover may still tint the
     * border — hover and focus are different states and a pointer user hovering
     * a field they have not focused is not being told anything false — but a
     * focused control must not paint a coloured border *and* a ring, because two
     * concentric chromatic edges read as a doubled halo and neither one is then
     * the focus indicator.
     *
     * This was not a style preference. The previous focus token was
     * `0 0 0 3px rgba(214, 47, 118, 0.32)` — a translucent pink glow that
     * composites to **1.63:1 against the paper canvas and 1.36:1 against the dark
     * canvas**. As a focus indicator it fails WCAG 2.1 §1.4.11 (3:1) on its own;
     * it only ever "passed" because `activeBorderColor` simultaneously turned the
     * border pink, i.e. the doubled halo the users complained about was
     * load-bearing for accessibility. Deleting the border change *without* making
     * the ring solid would have removed the only compliant half.
     *
     * So the ring is now **solid `--bk-ring`, 2px, at 2px offset**:
     *
     * The ring is achromatic in both themes (neutral[700] light, neutral[300]
     * dark) — a coral ring made every focused, still-empty field look invalid.
     * `antd-theme.test.ts` asserts the 3:1 floor on every surface, and
     * `a11y.test.ts` asserts the ring stays achromatic.
     *
     * Every surface clears 3:1, and the offset is what guarantees those are the
     * numbers that apply. The ring sits on the surface *behind* the control
     * rather than on the control's own fill — which matters because the fill is
     * the one ground the ring is not measured against: coral on the ink primary
     * is 3.36:1 and coral on the paper primary is **2.54:1**, so a flush ring on
     * a focused primary button in dark would be the one place the indicator
     * disappears. (Before the re-skin the same argument ran at 1.45:1 in both
     * themes; the number improved, the conclusion did not change.)
     *
     * `:focus-visible`, never `:focus` and never `:focus-within` — a pointer
     * click must not paint a ring, a Tab must.
     */
    focusRingWidth: "2px",
    focusRingOffset: "2px",
    borderWidth: "1px",
};
/** The printed edge: a 1px ink ring that, unlike a border, takes no layout. */
const webRing = (ink) => `0 0 0 1px ${ink}`;
/** A printed plate: the 1px ink ring plus a zero-blur offset that falls to the inline-end. */
const webPlate = (ink, px) => `${webRing(ink)}, calc(var(--bk-elev-sign, 1) * ${px}px) ${px}px 0 0 ${ink}`;
export const webTokens = {
    light: {
        surfacePaper: neutral[0],
        surfaceMist: slate[25],
        surfaceWash: coral[50],
        surfaceBlush: coral[100],
        surfaceInkBand: neutral[925],
        onInkBand: neutral[0],
        stage: slate[25],
        cardGround: neutral[0],
        rule: neutral[150],
        border: colors.light.border,
        elev0: webRing(colors.light.fg),
        elev1: webPlate(colors.light.fg, 2),
        elev2: webPlate(colors.light.fg, 4),
        elev3: webPlate(colors.light.fg, 6),
        rim: "none",
        glowCoral: "radial-gradient(60% 60% at 50% 40%, rgba(249, 125, 125, 0.16) 0%, rgba(249, 125, 125, 0) 70%)",
        halo: surfaceEffects.light.heroHalo,
    },
    dark: {
        surfacePaper: neutral[975],
        surfaceMist: neutral[950],
        // A warm ink, not coral[950]: the oxblood slab read as an error panel on
        // the procedure hero and the key-takeaways box. slate[800] carries the
        // coral's hue at C ≈ 0.006 and sits between neutral 950 and 925.
        surfaceWash: slate[800],
        surfaceBlush: coral[900],
        surfaceInkBand: neutral[1000],
        onInkBand: slate[50],
        stage: neutral[975],
        // One step above the mist (1.14:1), so a card lifts off a mist band and a
        // mist inset inside a card reads as recessed. At 950 the two were equal and
        // every inset vanished. The logo charcoal, exact.
        cardGround: neutral[925],
        // 1.40:1 on the card, 1.60:1 on mist, 1.87:1 on paper. slate[700] measured
        // 1.08:1 on the card and every in-card divider disappeared.
        rule: neutral[800],
        // The platform's dark `border` (#2A2625) is a frost value the panels draw
        // as an inset glass edge; as a solid stroke it is 1.01:1 on the card and
        // every input, option tile and framed row lost its edge. neutral[700] is
        // the mirror of light's border-on-paper (1.86:1): 1.95:1 on the card,
        // 2.23:1 on mist, 2.60:1 on paper.
        border: neutral[700],
        elev0: webRing(colors.dark.fg),
        elev1: webPlate(colors.dark.fg, 2),
        elev2: webPlate(colors.dark.fg, 4),
        elev3: webPlate(colors.dark.fg, 6),
        rim: "none",
        glowCoral: "radial-gradient(60% 60% at 50% 40%, rgba(249, 125, 125, 0.26) 0%, rgba(249, 125, 125, 0.08) 40%, rgba(11, 11, 11, 0) 72%)",
        halo: surfaceEffects.dark.heroHalo,
    },
};
/** Web radii (D7 reversed 2026-10-09): the broadsheet's shapes, the same in both themes. */
export const webRadii = {
    card: radii.md, // 8px
    pill: radii.full, // tags, badges, status pills only
    media: radii.md, // 8px — images sit in cards and share their corner
    control: radii.xs, // 4px — buttons, inputs, segmented controls
    cta: radii.none, // 0px — the large full-width block CTA
};
/**
 * The web type ladder (spec §3.1) — the hierarchy engine.
 *
 * Fluid steps are `clamp(phone, intercept + slope·vw, desktop)` solved for a
 * 375px phone and reaching the desktop size by ~1270px. Display leadings sit at
 * Kalameh's measured 1.5em content box: tighter clips ascenders and collides
 * stacked marks (see `typeScale`). Tracking is 0 at every step and is not a
 * token — Arabic-script glyphs join.
 */
export const webType = {
    displayXl: {
        size: "clamp(2.75rem, 1.9rem + 3.6vw, 4.75rem)",
        leading: "1.5",
        weight: fontWeight.black,
        family: "display",
    },
    display: {
        size: "clamp(2rem, 1.6rem + 1.7vw, 3rem)",
        leading: "1.5",
        weight: fontWeight.black,
        family: "display",
    },
    title: {
        size: "clamp(1.375rem, 1.24rem + 0.6vw, 1.75rem)",
        leading: "1.5",
        weight: fontWeight.bold,
        family: "display",
    },
    eyebrow: {
        size: "0.8125rem",
        leading: "1.6",
        weight: fontWeight.semibold,
        family: "body",
    },
    lead: {
        size: "clamp(1.125rem, 1.08rem + 0.2vw, 1.25rem)",
        leading: "1.9",
        weight: fontWeight.regular,
        family: "body",
    },
    body: {
        size: "1.0625rem",
        leading: "1.9",
        weight: fontWeight.regular,
        family: "body",
    },
    caption: {
        size: "clamp(0.8125rem, 0.79rem + 0.1vw, 0.875rem)",
        leading: "1.7",
        weight: fontWeight.regular,
        family: "body",
    },
};
/* ------------------------------------------------------------------ */
/*  The aggregate                                                      */
/* ------------------------------------------------------------------ */
export const tokens = {
    palette,
    colors,
    surfaceEffects,
    spacing,
    sectionSpacing,
    sectionSpacingLoose,
    density,
    radii,
    semanticRadii,
    shadows,
    shadowOffset,
    typography,
    motion,
    zIndex,
    breakpoints,
    containerMaxWidth,
    control,
};
//# sourceMappingURL=tokens.js.map